import { ComputerDesktopIcon, MoonIcon, SunIcon } from "@heroicons/react/24/outline"
import { Theme, useThemeStore } from "hooks/useTheme"
import { classNames } from "utils/ClassNames"

const options: { value: Theme; label: string; Icon: typeof SunIcon }[] = [
  { value: "light", label: "Light", Icon: SunIcon },
  { value: "dark", label: "Dark", Icon: MoonIcon },
  { value: "system", label: "System", Icon: ComputerDesktopIcon }
]

export interface ThemeToggleProps {
  /** Render as a single cycling icon button (for the collapsed sidebar rail). */
  collapsed?: boolean
}

/** Light / Dark / System theme switcher backed by the persisted theme store. */
export const ThemeToggle = ({ collapsed = false }: ThemeToggleProps) => {
  const theme = useThemeStore((state) => state.theme)
  const setTheme = useThemeStore((state) => state.setTheme)

  if (collapsed) {
    const current = options.find((o) => o.value === theme) ?? options[2]
    const cycle = () => {
      const next = options[(options.indexOf(current) + 1) % options.length]
      setTheme(next.value)
    }
    return (
      <button
        type="button"
        onClick={cycle}
        title={`Theme: ${current.label}`}
        className="flex w-full justify-center rounded-md p-1.5 text-gray-400 hover:bg-gray-50 hover:text-primary-600 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-primary-400"
      >
        <current.Icon aria-hidden="true" className="h-6 w-6" />
        <span className="sr-only">Change theme (current: {current.label})</span>
      </button>
    )
  }

  return (
    <div className="flex items-center gap-1 rounded-lg bg-gray-100 p-1 dark:bg-gray-800">
      {options.map((option) => {
        const active = theme === option.value
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => setTheme(option.value)}
            title={option.label}
            className={classNames(
              "flex flex-1 items-center justify-center rounded-md p-1.5 transition-colors",
              active
                ? "bg-white text-primary-600 shadow-sm dark:bg-gray-700 dark:text-primary-400"
                : "text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
            )}
          >
            <option.Icon aria-hidden="true" className="h-5 w-5" />
            <span className="sr-only">{option.label}</span>
          </button>
        )
      })}
    </div>
  )
}
